import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import type React from 'react';
import {
  Box,
  Button,
  IconArrow,
  IconTick,
  Inline,
  MenuItem,
  MenuRenderer,
  useResponsiveValue,
} from 'braid-design-system';
import {
  type Bucket,
  BUCKETS,
} from '../../../stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import { Display } from 'src/libs/Display';

interface Props {
  bucket: Bucket;
  placement?: 'top' | 'bottom';
  fullName: string;
  onStatusChange: (bucket: Bucket) => void;
}

export const StatusMenu: React.FC<Props> = ({
  onStatusChange,
  placement,
  bucket,
  fullName,
}) => {
  const { getLabelFromBucket } = useBucketLabel();
  const { t } = useTranslations(translations);
  const options = Object.values(BUCKETS)
    .filter((option) => option !== BUCKETS.UNKNOWN)
    .map((option) => {
      const isCurrentBucket = option === bucket;

      // TODO: Disable current bucket so the user cannot click on it
      // Currently not supported by Braid so we chose to show the option instead of hiding it.
      // Discussion: https://seekchat.slack.com/archives/CNYJ5PN48/p1681790096402469?thread_ts=1681774950.096869&cid=CNYJ5PN48
      return (
        <MenuItem
          data={{ cy: `status-menu-item-${option}` }}
          key={option}
          onClick={() => {
            if (!isCurrentBucket) {
              onStatusChange(option);
            }
          }}
        >
          <span>{getLabelFromBucket(option)}</span>{' '}
          {isCurrentBucket && <IconTick />}
        </MenuItem>
      );
    });
  const responsiveValue = useResponsiveValue();

  return (
    <MenuRenderer
      placement={placement}
      width="small"
      offsetSpace="xxsmall"
      data={{ cy: 'status-menu' }}
      align={responsiveValue({ mobile: 'left', tablet: 'right' }) ?? 'right'}
      trigger={(triggerProps) => (
        <Button
          size="small"
          tone="neutral"
          variant={bucket === 'INBOX' || bucket === '' ? 'solid' : 'ghost'}
          {...triggerProps}
          aria-label={
            bucket === 'INBOX' || bucket === ''
              ? t('Move fullName to another status', { fullName })
              : t('Move fullName from bucket', {
                  fullName,
                  bucket: getLabelFromBucket(mapBucketTypeToBuckets(bucket)),
                })
          }
        >
          {bucket === 'INBOX' || bucket === '' ? (
            <Inline
              space={
                responsiveValue({ mobile: 'xsmall', tablet: 'none' }) ?? 'none'
              }
              align="center"
            >
              <Display mobile>{t('Progress')}</Display>
              <IconArrow direction="right" />
            </Inline>
          ) : (
            <Inline space="xsmall" align="center">
              <Box aria-hidden>
                {getLabelFromBucket(mapBucketTypeToBuckets(bucket))}
              </Box>
              <IconArrow direction="right" />
            </Inline>
          )}
        </Button>
      )}
    >
      {options}
    </MenuRenderer>
  );
};
